// ============================================================================
// ASSETS DOMAIN API CLIENT
// ============================================================================
// Domain-specific API for companies, users, and physical assets
// ============================================================================

import { ApiClient } from '../../../core/apiClient';
import { Company, User, CompanyFormData, UserFormData } from '../../../types';

export interface CompaniesHouseResult {
  company_number: string;
  company_name: string;
  company_status: string;
  company_type: string;
  date_of_creation?: string;
  registered_office_address?: {
    address_line_1?: string;
    address_line_2?: string;
    locality?: string;
    region?: string;
    postal_code?: string;
    country?: string;
  };
  sic_codes?: string[];
}

export interface BillingPlan {
  id: string;
  name: string;
  description?: string;
  tier?: string;
  price?: number;
  currency?: string;
  billing_cycle?: string;
}

class AssetsApi extends ApiClient {
  constructor() {
    super({
      enableLogging: import.meta.env.DEV,
    });
  }

  // Companies
  async getCompanies(params?: { industry?: string }): Promise<Company[]> {
    const response = await this.client.get('/companies', { params });
    return response.data.data || response.data || [];
  }

  async createCompany(data: CompanyFormData): Promise<{ id: number }> {
    const response = await this.client.post('/companies', data);
    const id = response.data?.data?.id ?? response.data?.id;
    if (!id) {
      throw new Error('Company created but response did not include an ID.');
    }
    return { id: Number(id) };
  }

  async getCompany(id: number): Promise<Company> {
    const response = await this.client.get(`/companies/${id}`);
    return response.data.data || response.data;
  }

  async updateCompany(id: number, data: Partial<CompanyFormData>): Promise<void> {
    await this.client.put(`/companies/${id}`, data);
  }

  async deleteCompany(id: number, exportData: boolean = false): Promise<any> {
    const response = await this.client.delete(`/companies/${id}`, {
      params: { exportData }
    });
    return response.data;
  }

  async getBillingPlans(): Promise<BillingPlan[]> {
    const response = await this.client.get('/billing/plans');
    const plans = response.data?.plans || response.data?.data || response.data || [];
    return Array.isArray(plans) ? plans : [];
  }

  // Companies House integration
  async searchCompaniesHouse(query: string): Promise<{
    success: boolean;
    configured: boolean;
    data?: CompaniesHouseResult[];
    message?: string;
  }> {
    const response = await this.client.get('/companies/companies-house/search', {
      params: { q: query }
    });
    return response.data;
  }

  async getCompaniesHouseProfile(companyNumber: string): Promise<{
    success: boolean;
    data?: {
      profile: any;
      mapped: {
        name: string;
        legal_name: string;
        registration_number: string;
        address: string;
        industry: string;
        companies_house_status: string;
        companies_house_type: string;
        date_of_creation?: string;
      };
    };
    message?: string;
  }> {
    const response = await this.client.get(`/companies/companies-house/${encodeURIComponent(companyNumber)}`);
    return response.data;
  }

  // Users
  async getUsers(params?: { role?: string; company_id?: number }): Promise<User[]> {
    const response = await this.client.get('/users', { params });
    return response.data.data || response.data || [];
  }

  async createUser(data: UserFormData): Promise<{ id: number; default_password: string }> {
    const response = await this.client.post('/users', data);
    return response.data;
  }

  async updateUser(id: number, data: Partial<UserFormData>): Promise<void> {
    await this.client.put(`/users/${id}`, data);
  }



  // Physical Assets (Vehicles, Facilities, Equipment)
  async getAssets(params?: {
    company_id?: number;
    asset_type?: string;
    status?: string;
  }): Promise<any[]> {
    const response = await this.client.get('/assets', { params });
    return response.data.data || response.data || [];
  }

  async getAsset(id: number): Promise<any> {
    const response = await this.client.get(`/assets/${id}`);
    return response.data;
  }

  async createAsset(data: {
    company_id: number;
    asset_type: string;
    name: string;
    description?: string;
    location?: string;
    status?: string;
    metadata?: Record<string, any>;
  }): Promise<{ id: number }> {
    const response = await this.client.post('/assets', data);
    return response.data;
  }

  async updateAsset(id: number, data: Partial<{
    name: string;
    description: string;
    location: string;
    status: string;
    metadata: Record<string, any>;
  }>): Promise<void> {
    await this.client.put(`/assets/${id}`, data);
  }

  async deleteAsset(id: number): Promise<void> {
    await this.client.delete(`/assets/${id}`);
  }

  // Asset Types
  async getAssetTypes(): Promise<Array<{ value: string; label: string }>> {
    const response = await this.client.get('/assets/types');
    return response.data;
  }
}

export const assetsApi = new AssetsApi();
export default assetsApi;
